import type { ReactElement } from 'react';

/**
 * The inline icon set shared across screens (spec §9.4): hand-drawn, a
 * single 1.5px `currentColor` stroke, round caps and joins, all on the same
 * 24x24 grid so they sit consistently at any size a caller picks.
 */
export const ICON_NAMES = [
  'undo',
  'redo',
  'check',
  'chevron',
  'grip',
  'upload',
  'close',
  'copy',
  'menu',
  'plus'
] as const;

export type IconName = (typeof ICON_NAMES)[number];

const SHAPES: Record<IconName, ReactElement> = {
  undo: (
    <>
      <path d="M9 6 4 11l5 5" />
      <path d="M4 11h10a6 6 0 1 1-6 6" />
    </>
  ),
  redo: (
    <>
      <path d="M15 6l5 5-5 5" />
      <path d="M20 11H10a6 6 0 1 0 6 6" />
    </>
  ),
  check: <path d="M5 12.5 9.5 17 19 7" />,
  chevron: <path d="M6 9l6 6 6-6" />,
  grip: (
    <>
      <circle cx="9" cy="6" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="6" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="18" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="18" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M5 15v3.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V15" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  copy: (
    <>
      <rect x="4" y="7" width="12" height="13" rx="1.5" />
      <path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h9A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H17" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  )
};

export function Icon(props: { name: IconName; size?: number }): ReactElement {
  const size = props.size ?? 18;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {SHAPES[props.name]}
    </svg>
  );
}
